import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal, ViewChild, ElementRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);
import { ActivatedRoute } from '@angular/router';
import { finalize } from 'rxjs/operators';
import {
  ReportColumn,
  ReportResult,
  ReportRow,
  ReportType,
  ReportsService,
} from '../../../services/reports/reports.service';
import { StoreService } from '../../../services/stores/store.service';
import { buildCsv, downloadCsv } from '../../../utils/csv-export.util';

@Component({
  selector: 'app-report-page',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './report-page.component.html',
})
export class ReportPageComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private reportsService = inject(ReportsService);
  private storeService = inject(StoreService);

  reportType = signal<ReportType>('daily-orders');
  selectedRange = signal(1);
  selectedStoreId = signal<string | null>(null);
  stores = signal<{ id: string, name: string }[]>([]);
  currentPage = signal(1);
  pageSize = 12;
  isLoading = signal(false);
  error = signal('');
  exporting = signal(false);
  exportProgress = signal('');
  private loadedRange?: { from: Date; to: Date };
  private loadedStore?: string;
  private loadedType?: ReportType;
  private loadVersion = 0;
  report = signal<ReportResult | null>(null);

  startDate = signal<string>(new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0]);
  endDate = signal<string>(new Date().toISOString().split('T')[0]);

  @ViewChild('reportChart') reportChart!: ElementRef<HTMLCanvasElement>;
  private chartInstance: Chart | null = null;
  chartData = signal<{ labels: string[], values: number[] }>({ labels: [], values: [] });

  totalPages = computed(() =>
    Math.max(1, Math.ceil((this.report()?.total || 0) / this.pageSize))
  );

  paginatedRows = computed(() => this.report()?.rows || []);

  hasRows = computed(() => (this.report()?.rows.length || 0) > 0);

  ngOnInit(): void {
    this.route.data.subscribe((data) => {
      this.reportType.set((data['reportType'] as ReportType) || 'daily-orders');
      this.currentPage.set(1);
      this.loadReport();
    });
    this.loadStores();
  }

  private loadStores(): void {
    this.storeService.allStores().subscribe((stores) => {
      this.stores.set(stores.map(s => ({ 
        id: String(s.id), 
        name: s.name || 'Unknown Store' 
      })));
    });
  }

  onRangeChange(days: string | number): void {
    this.selectedRange.set(Number(days));
    this.currentPage.set(1);
    this.loadReport();
  }

  onStoreChange(storeId: string | null): void {
    this.selectedStoreId.set(storeId);
    this.currentPage.set(1);
    this.loadReport();
  }

  onCustomDateChange(): void {
    if (this.selectedRange() === -1) {
      this.currentPage.set(1);
      this.loadReport();
    }
  }

  loadReport(): void {
    const version = ++this.loadVersion;
    this.isLoading.set(true);
    this.error.set('');

    const range = this.selectedRange();
    let customRange = undefined;

    if (range === -1) {
      customRange = {
        from: new Date(this.startDate() + 'T00:00:00+05:30'),
        to: new Date(this.endDate() + 'T23:59:59.999+05:30')
      };
    }
    const queryRange = customRange || this.reportsService.getRange(range);
    if (!Number.isFinite(queryRange.from.getTime()) || !Number.isFinite(queryRange.to.getTime()) || queryRange.to < queryRange.from) {
      this.error.set('Choose a valid start and end date.');
      this.isLoading.set(false);
      this.report.set(null);
      return;
    }
    const queryStore = this.selectedStoreId() || undefined;
    const queryType = this.reportType();

    this.reportsService
      .getReport(
        queryType, range, queryRange,
        queryStore, this.currentPage(), this.pageSize
      )
      .pipe(finalize(() => { if (version === this.loadVersion) this.isLoading.set(false); }))
      .subscribe({
        next: (report) => {
          if (version !== this.loadVersion) return;
          this.loadedRange = queryRange;
          this.loadedStore = queryStore;
          this.loadedType = queryType;
          this.report.set(report);
          // Initialize chart after view renders
          setTimeout(() => this.initChart(), 100);
        },
        error: (error: unknown) => {
          if (version !== this.loadVersion) return;
          this.report.set(null);
          this.error.set(this.errorMessage(error));
        },
      });
  }

  nextPage(): void {
    if (this.currentPage() < this.totalPages()) {
      this.currentPage.update((page) => page + 1);
      this.loadReport();
    }
  }

  prevPage(): void {
    if (this.currentPage() > 1) {
      this.currentPage.update((page) => page - 1);
      this.loadReport();
    }
  }

  cell(row: ReportRow, column: ReportColumn): string | number {
    return row[column.key] ?? '-';
  }

  cardClass(tone: string | undefined): string {
    switch (tone) {
      case 'success':
        return 'border-success/20 bg-success/5';
      case 'warning':
        return 'border-warning/20 bg-warning/5';
      case 'error':
        return 'border-error/20 bg-error/5';
      case 'info':
        return 'border-info/20 bg-info/5';
      default:
        return 'border-base-300 bg-base-100';
    }
  }

  exportCsv(): void {
    const report = this.report();
    if (!report?.rows.length) return;

    downloadCsv(`${report.exportFileName}-page-${this.currentPage()}.csv`, buildCsv(
      report.columns.map(column => column.label),
      report.rows.map(row => report.columns.map(column => row[column.key]))
    ));
  }

  async exportAll(): Promise<void> {
    if (this.exporting() || this.isLoading() || !this.loadedRange || !this.loadedType) return;
    this.exporting.set(true);
    this.error.set('');
    this.exportProgress.set('Preparing export');
    const type = this.loadedType;
    const range = this.loadedRange;
    const store = this.loadedStore;
    try {
      const csv = await this.reportsService.exportAll(type, range, store,
        (loaded, total) => this.exportProgress.set(`${loaded} / ${total}`));
      downloadCsv(`${type}-store-${store || 'all'}-${range.from.toISOString().slice(0,10)}-to-${range.to.toISOString().slice(0,10)}.csv`, csv);
    } catch (error) {
      this.error.set(this.errorMessage(error));
    } finally {
      this.exporting.set(false);
      this.exportProgress.set('');
    }
  }

  private errorMessage(error: unknown): string {
    if (error instanceof Error) return error.message;
    if (typeof error === 'string') return error;
    return 'Unable to load report data.';
  }

  initChart(): void {
    if (!this.reportChart) return;
    if (this.chartInstance) {
      this.chartInstance.destroy();
    }
    const reportData = this.report();
    if (!reportData || reportData.rows.length === 0) return;

    const ctx = this.reportChart.nativeElement.getContext('2d');
    if (!ctx) return;

    // Use the first text column as label, and the first numeric column as data
    const labels = reportData.rows.slice().reverse().map(r => r[reportData.columns[0].key]);
    
    // Find best column for Y axis (Value, Amount, Count, Units)
    const valueColumn = reportData.columns.find(c => {
      const k = c.key.toLowerCase();
      return k.includes('total') || k.includes('amount') || k.includes('count') || k.includes('units') || k.includes('revenue');
    }) || reportData.columns[1] || reportData.columns[0];

    const dataPoints = reportData.rows.slice().reverse().map(r => {
      const val = r[valueColumn.key];
      if (typeof val === 'string') {
        // Remove currency symbols and commas
        return parseFloat(val.replace(/[₹,]/g, '')) || 0;
      }
      return Number(val) || 0;
    });

    this.chartInstance = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: labels as string[],
        datasets: [{
          label: valueColumn.label,
          data: dataPoints,
          backgroundColor: 'rgba(56, 189, 248, 0.4)', 
          borderColor: '#0ea5e9', 
          borderWidth: 2,
          borderRadius: 6,
          hoverBackgroundColor: 'rgba(56, 189, 248, 0.7)',
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: 'rgba(0,0,0,0.8)',
            padding: 12,
            cornerRadius: 8,
            titleFont: { size: 14, weight: 'bold' },
            bodyFont: { size: 13 },
            callbacks: {
              label: (context) => {
                const val = context.parsed.y ?? 0;
                return ` ${valueColumn.label}: ${val.toLocaleString()}`;
              }
            }
          }
        },
        scales: {
          y: { 
            beginAtZero: true, 
            grid: { color: 'rgba(0,0,0,0.05)' },
            border: { display: false },
            ticks: { font: { size: 10 } }
          },
          x: { 
            grid: { display: false },
            border: { display: false },
            ticks: { font: { size: 10 }, maxRotation: 45, minRotation: 0 }
          }
        }
      }
    });
  }
}
